export const VIEW_AS_STORAGE_KEY = "cur8:viewAs";

export const VIEW_AS_SESSION_ID_PATTERN =
  "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}";

const SESSION_ID = new RegExp(`^${VIEW_AS_SESSION_ID_PATTERN}$`);

export const isViewAsSessionId = (value: unknown): value is string =>
  typeof value === "string" && SESSION_ID.test(value);

export interface StoredViewAs {
  sessionId: string;
  /** Known once the app has fetched the session; the head script drops a stale one. */
  expiresAt?: string;
}

// Unreadable storage (private mode, blocked site data) says nothing about the session, so it is not "empty".
export type StoredViewAsRead =
  | { available: true; session: StoredViewAs | null }
  | { available: false };

export const readStoredViewAs = (): StoredViewAsRead => {
  if (typeof window === "undefined") return { available: false };

  try {
    const stored = window.localStorage.getItem(VIEW_AS_STORAGE_KEY);
    if (!stored) return { available: true, session: null };

    const parsed = JSON.parse(stored) as {
      sessionId?: unknown;
      expiresAt?: unknown;
    };
    if (!isViewAsSessionId(parsed.sessionId)) {
      return { available: true, session: null };
    }

    return {
      available: true,
      session: {
        sessionId: parsed.sessionId,
        ...(typeof parsed.expiresAt === "string"
          ? { expiresAt: parsed.expiresAt }
          : {}),
      },
    };
  } catch {
    return { available: false };
  }
};

// The head script drops a stored session the moment it expires, so nothing else may treat one as live.
export const isStoredViewAsLive = (session: StoredViewAs): boolean => {
  if (typeof session.expiresAt !== "string") return true;

  const expiresAt = Date.parse(session.expiresAt);
  return Number.isNaN(expiresAt) || expiresAt > Date.now();
};

export const readStoredViewAsSessionId = (): string | null => {
  const read = readStoredViewAs();
  return read.available ? read.session?.sessionId ?? null : null;
};

/** Records when the stored session ends, so a later page load can drop a stale id. */
export const storeViewAsExpiry = (
  sessionId: string,
  expiresAt: string
): void => {
  const read = readStoredViewAs();
  if (!read.available || read.session?.sessionId !== sessionId) return;

  try {
    window.localStorage.setItem(
      VIEW_AS_STORAGE_KEY,
      JSON.stringify({ sessionId, expiresAt })
    );
  } catch {
    // Private mode and blocked site data deny storage access.
  }
};

export const clearViewAsSession = (): void => {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.removeItem(VIEW_AS_STORAGE_KEY);
  } catch {
    // Private mode and blocked site data deny storage access.
  }
};
